/**
 * <glossary-list> — rendu côté client du glossaire INCI.
 * Le bloc Liquid expose les 438 entrées via shop.metafields.custom.glossary_data (json)
 * en JSON inline dans un <script type="application/json">.
 * Ce JS parse, groupe par alphabet_letter, rend tout en HTML.
 * Contourne la limite Shopify Liquid 50 items sur metaobjects.values.
 */
class GlossaryList extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('script[type="application/json"]');
    if (!dataEl) {
      console.warn('[glossary-list] no JSON data found');
      return;
    }
    let items;
    try { items = JSON.parse(dataEl.textContent); }
    catch (e) { console.error('[glossary-list] JSON parse failed', e); return; }
    if (!Array.isArray(items) || !items.length) return;

    // Lire les settings depuis data-* du wrapper
    const opts = {
      accordionMode: this.dataset.accordionMode === 'true',
      showLetterHeading: this.dataset.showLetterHeading === 'true',
      showInci: this.dataset.showInci === 'true',
      showDefinition: this.dataset.showDefinition === 'true',
      showFunction: this.dataset.showFunction === 'true',
      showOrigin: this.dataset.showOrigin === 'true',
      showFoundIn: this.dataset.showFoundIn === 'true',
      showSafety: this.dataset.showSafety === 'true',
      showEnvironment: this.dataset.showEnvironment === 'true',
      showBlozennPlus: this.dataset.showBlozennPlus === 'true',
      showReadingTime: this.dataset.showReadingTime === 'true',
      showVerdict: this.dataset.showVerdict === 'true',
      showSeparatorTop: this.dataset.showSeparatorTop === 'true',
      showSeparatorBottom: this.dataset.showSeparatorBottom === 'true',
      inciLabel: this.dataset.inciLabel || 'Nom INCI :',
      labelFunction: this.dataset.labelFunction || '🖊️ Fonction —',
      labelOrigin: this.dataset.labelOrigin || '📍 Origine —',
      labelFoundIn: this.dataset.labelFoundIn || '🧴 On le trouve dans —',
      labelSafety: this.dataset.labelSafety || '⚠️ Sécurité —',
      labelEnvironment: this.dataset.labelEnvironment || '🌍 Environnement —',
      labelBlozennPlus: this.dataset.labelBlozennPlus || '💚 Le + Blozenn',
      labelReadingTime: this.dataset.labelReadingTime || 'Temps de lecture :',
      readingTimeIcon: this.dataset.readingTimeIcon || '🕐',
    };

    // Grouper par alphabet_letter (items déjà triés côté Python)
    const groups = new Map();
    for (const it of items) {
      const letter = (it.l || (it.n || '?')[0] || '').toUpperCase();
      if (!groups.has(letter)) groups.set(letter, []);
      groups.get(letter).push(it);
    }

    const container = this.querySelector('.glossary-list__container');
    if (!container) return;

    const frag = document.createDocumentFragment();
    for (const [letter, list] of groups) {
      const section = document.createElement('section');
      section.className = 'glossary-list__group';
      section.id = `letter-${letter}`;
      section.setAttribute('aria-labelledby', `letter-heading-${letter}`);

      if (opts.showLetterHeading) {
        const h2 = document.createElement('h2');
        h2.className = 'glossary-list__group-letter';
        h2.id = `letter-heading-${letter}`;
        h2.textContent = letter;
        section.appendChild(h2);
      }

      const groupItems = document.createElement('div');
      groupItems.className = 'glossary-list__group-items';

      for (const it of list) {
        groupItems.appendChild(renderEntry(it, opts));
      }

      section.appendChild(groupItems);
      frag.appendChild(section);
    }
    container.replaceChildren(frag);

    // Ancres profondes : /pages/glossaire#ing-<handle> (redirections 301 depuis /inci/<handle>)
    // Le rendu est client-side : l'anchor-scroll natif du navigateur a déjà échoué au load,
    // donc on le rejoue ici (+ ouverture du <details> ciblé).
    const openFromHash = () => {
      const id = decodeURIComponent((location.hash || '').slice(1));
      if (!id) return;
      let el = null;
      try { el = this.querySelector(`[id="${CSS.escape(id)}"]`); } catch (e) { /* hash exotique */ }
      if (!el) return;
      if (el.tagName === 'DETAILS') el.open = true;
      // scrollIntoView est annulé par scroll-behavior:smooth sur les longues distances → scroll calculé instant
      const y = el.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top: y, behavior: 'instant' });
    };
    // 1er passage immédiat (ouvre le details), re-scroll quand le layout est stable
    openFromHash();
    if (document.readyState === 'complete') {
      setTimeout(openFromHash, 150);
    } else {
      window.addEventListener('load', () => setTimeout(openFromHash, 150), { once: true });
    }
    window.addEventListener('hashchange', openFromHash);
  }
}

function renderEntry(it, opts) {
  const body = renderBody(it, opts);
  if (opts.accordionMode) {
    const details = document.createElement('details');
    details.className = 'glossary-list__item glossary-list__item--accordion';
    details.id = `ing-${it.h || ''}`;
    const summary = document.createElement('summary');
    summary.className = 'glossary-list__item-summary';
    const txt = document.createElement('span');
    txt.className = 'glossary-list__summary-text';
    const name = document.createElement('span');
    name.className = 'glossary-list__name';
    name.textContent = it.n;
    txt.appendChild(name);
    if (opts.showInci && it.i) {
      const inci = document.createElement('span');
      inci.className = 'glossary-list__summary-inci';
      inci.textContent = '— ' + it.i;
      txt.appendChild(inci);
    }
    summary.appendChild(txt);
    const chev = document.createElement('span');
    chev.className = 'glossary-list__chevron';
    chev.setAttribute('aria-hidden', 'true');
    chev.textContent = '+';
    summary.appendChild(chev);
    details.appendChild(summary);
    const content = document.createElement('div');
    content.className = 'glossary-list__item-content';
    content.innerHTML = body;
    details.appendChild(content);
    return details;
  }
  const article = document.createElement('article');
  article.className = 'glossary-list__item glossary-list__item--flat';
  article.id = `ing-${it.h || ''}`;
  const h3 = document.createElement('h3');
  h3.className = 'glossary-list__name';
  h3.textContent = it.n;
  article.appendChild(h3);
  const wrap = document.createElement('div');
  wrap.innerHTML = body;
  while (wrap.firstChild) article.appendChild(wrap.firstChild);
  return article;
}

function renderBody(it, opts) {
  const parts = [];
  if (opts.showInci && it.i) {
    parts.push(`<p class="glossary-list__inci"><strong>${escapeHtml(opts.inciLabel)}</strong> ${escapeHtml(it.i)}</p>`);
  }
  if (opts.showSeparatorTop) parts.push('<hr class="glossary-list__sep">');
  if (opts.showDefinition && it.d) {
    parts.push(`<div class="glossary-list__definition">${it.d}</div>`);
  }
  if (opts.showFunction && it.f) parts.push(field(opts.labelFunction, it.f));
  if (opts.showOrigin && it.o) parts.push(field(opts.labelOrigin, it.o));
  if (opts.showFoundIn && it.fi) parts.push(field(opts.labelFoundIn, it.fi));
  if (opts.showSafety && it.s) parts.push(field(opts.labelSafety, it.s));
  if (opts.showEnvironment && it.e) parts.push(field(opts.labelEnvironment, it.e));
  if (opts.showBlozennPlus && it.p) {
    parts.push(`<div class="glossary-list__plus">${opts.labelBlozennPlus ? `<p class="glossary-list__field-label">${escapeHtml(opts.labelBlozennPlus)}</p>` : ''}${it.p}</div>`);
  }
  if (opts.showSeparatorBottom) parts.push('<hr class="glossary-list__sep">');
  const meta = [];
  if (opts.showReadingTime && it.t) {
    meta.push(`<span class="glossary-list__reading">${escapeHtml(opts.readingTimeIcon)} ${escapeHtml(opts.labelReadingTime)} ${escapeHtml(it.t)}</span>`);
  }
  if (opts.showVerdict && it.v) {
    let stars = '';
    if (it.v.includes('3')) stars = '⭐⭐⭐';
    else if (it.v.includes('2')) stars = '⭐⭐';
    else if (it.v.includes('1')) stars = '⭐';
    if (stars) meta.push(`<span class="glossary-list__verdict" aria-label="Verdict">${stars}</span>`);
  }
  if (meta.length) parts.push(`<div class="glossary-list__meta">${meta.join('')}</div>`);
  return parts.join('');
}

function field(label, value) {
  return `<p class="glossary-list__field"><span class="glossary-list__field-label">${escapeHtml(label)}</span> ${escapeHtml(value).replace(/\n/g, '<br>')}</p>`;
}

function escapeHtml(s) {
  return String(s == null ? '' : s).replace(/[<>&"']/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' }[c]));
}

if (!customElements.get('glossary-list')) {
  customElements.define('glossary-list', GlossaryList);
}
